import type { Category } from './category';
import type { Discount } from './discount';
import type { MenuItem } from './menu-item';
import {
  UPGRADE_TIER_STAGES,
  type UpgradeTierStage,
  tierCardDisplayTitleForStage,
} from './menu-upgrade-tier-title.util';
import {
  type OrderMenuItem,
  type OrderMenuItemModifier,
  OrderMenuItemModifierType,
  type OrderMenuItemUpgrade,
} from './order';
import type { Upgrade } from './upgrade';

/** Unit surcharge for one menu-item order line (modifier unit price × selected option quantity). */
export function orderMenuItemModifierUnitTotal(modifier: OrderMenuItemModifier): number {
  const optionQuantity = Math.max(modifier.quantity ?? 1, 1);
  return modifier.price * optionQuantity;
}

export function orderMenuItemExtrasVariationsUnit(item: OrderMenuItem): number {
  const extras = item.extras?.reduce((sum, extraRow) => sum + extraRow.price, 0) ?? 0;
  const variations = item.variation_options?.reduce((sum, vo) => sum + vo.price, 0) ?? 0;
  return extras + variations;
}

export function orderMenuItemNonUpgradeModifiersUnit(item: OrderMenuItem): number {
  return (
    item.modifiers?.reduce((sum, modifierRow) => {
      if (modifierRow.type === OrderMenuItemModifierType.UPGRADE) {
        return sum;
      }
      return sum + orderMenuItemModifierUnitTotal(modifierRow);
    }, 0) ?? 0
  );
}

export function getOrderItemUpgradeModifiers(item: OrderMenuItem): OrderMenuItemUpgrade[] {
  const fromModifiers = (item.modifiers ?? []).filter(
    (modifier): modifier is OrderMenuItemUpgrade =>
      modifier.type === OrderMenuItemModifierType.UPGRADE
  );
  if (fromModifiers.length > 0) {
    return fromModifiers;
  }
  return (item.upgrades ?? []).map((row) => ({
    id: row.id,
    type: OrderMenuItemModifierType.UPGRADE,
    stage: row.stage,
    price: row.price,
  }));
}

export function hasMenuUpgradeMainLine(item: OrderMenuItem): boolean {
  return getOrderItemUpgradeModifiers(item).length > 0;
}

function bundledFixedDiscountRunningTotal(item: OrderMenuItem): number | null {
  const fixed = (item.discounts ?? []).filter(
    (discount): discount is Discount & { type: 'FIXED' } => discount.type === 'FIXED'
  );
  if (fixed.length === 0) {
    return null;
  }
  return Math.max(...fixed.map((discountRow) => discountRow.amount));
}

function looksLikeIncludedUpgradeLine(item: OrderMenuItem): boolean {
  const runningTotal = bundledFixedDiscountRunningTotal(item);
  if (runningTotal == null) {
    return false;
  }
  return runningTotal >= (item.price ?? 0) - 1e-9;
}

export function normalizeMenuItemsOrderForUpgradeBundles(
  menuItems: OrderMenuItem[]
): OrderMenuItem[] {
  if (menuItems.length <= 1) {
    return menuItems;
  }
  const hasUpgradeMainAnywhere = menuItems.some((row) => hasMenuUpgradeMainLine(row));
  if (!hasUpgradeMainAnywhere) {
    return menuItems;
  }

  const runningTotalRank = (item: OrderMenuItem): number => {
    const runningTotal = bundledFixedDiscountRunningTotal(item);
    return runningTotal ?? Number.POSITIVE_INFINITY;
  };

  const scoreRow = (item: OrderMenuItem): number => {
    if (hasMenuUpgradeMainLine(item)) {
      return 0;
    }
    if (hasUpgradeMainAnywhere && looksLikeIncludedUpgradeLine(item)) {
      return 1;
    }
    return 2;
  };

  return [...menuItems].sort((rowA, rowB) => {
    const tierDiff = scoreRow(rowA) - scoreRow(rowB);
    if (tierDiff !== 0) {
      return tierDiff;
    }
    const cumulativeDiscountDiff = runningTotalRank(rowA) - runningTotalRank(rowB);
    if (cumulativeDiscountDiff !== 0) {
      return cumulativeDiscountDiff;
    }
    return String(rowA.id ?? '').localeCompare(String(rowB.id ?? ''));
  });
}

function cumulativeBundlePrevAmount(
  menuItems: OrderMenuItem[],
  precedingMainIndex: number,
  itemIndex: number
): number {
  let previousRunning = 0;
  for (let lineIndex = precedingMainIndex + 1; lineIndex < itemIndex; lineIndex += 1) {
    const runningTotal = bundledFixedDiscountRunningTotal(menuItems[lineIndex]);
    if (runningTotal != null) {
      previousRunning = runningTotal;
    }
  }
  return previousRunning;
}

function findPrecedingUpgradeMainIndex(
  menuItems: OrderMenuItem[],
  itemIndex: number
): number | null {
  for (let lineIndex = itemIndex - 1; lineIndex >= 0; lineIndex -= 1) {
    if (hasMenuUpgradeMainLine(menuItems[lineIndex])) {
      return lineIndex;
    }
  }
  return null;
}

function canApplyUpgradeBundleAdditivePriceShape(item: OrderMenuItem): boolean {
  return !item.variation_options?.length && !item.extras?.length;
}

export function menuItemIsUpgradeBundleIncludedLine(
  menuItems: OrderMenuItem[],
  itemIndex: number
): boolean {
  const item = menuItems[itemIndex];
  if (hasMenuUpgradeMainLine(item)) {
    return false;
  }
  const precedingMainIndex = findPrecedingUpgradeMainIndex(menuItems, itemIndex);
  const runningTotal = bundledFixedDiscountRunningTotal(item);
  return (
    precedingMainIndex != null &&
    runningTotal != null &&
    canApplyUpgradeBundleAdditivePriceShape(item)
  );
}

export function resolveOrderMenuUpgradeTierDisplayLabel(
  orderItem: OrderMenuItem,
  orderMenuItems: OrderMenuItem[],
  itemIndex: number,
  catalogUpgrades: Upgrade[],
  allMenuItems: MenuItem[],
  categories: Category[],
  languageCode: string
): string {
  let upgradeId = '';
  let stage = 0;
  const upgradeModifiers = getOrderItemUpgradeModifiers(orderItem);
  if (upgradeModifiers.length > 0) {
    upgradeId = upgradeModifiers[0].id;
    stage = upgradeModifiers[0].stage;
  } else if (menuItemIsUpgradeBundleIncludedLine(orderMenuItems, itemIndex)) {
    const precedingMainIndex = findPrecedingUpgradeMainIndex(orderMenuItems, itemIndex);
    if (precedingMainIndex == null) {
      return '';
    }
    const mainLine = orderMenuItems[precedingMainIndex];
    const mainModifiers = getOrderItemUpgradeModifiers(mainLine);
    if (mainModifiers.length === 0) {
      return '';
    }
    upgradeId = mainModifiers[0].id;
    stage = mainModifiers[0].stage;
  } else {
    return '';
  }
  const catalogRow = catalogUpgrades.find((upgradeRow) => upgradeRow.id === upgradeId);
  if (!catalogRow) {
    return '';
  }
  const stageNumber = Number(stage);
  if (!UPGRADE_TIER_STAGES.includes(stageNumber as UpgradeTierStage)) {
    return '';
  }
  return tierCardDisplayTitleForStage(
    catalogRow,
    stageNumber as UpgradeTierStage,
    languageCode,
    allMenuItems,
    categories
  ).trim();
}

export function getOrderMenuItemUnitNet(menuItems: OrderMenuItem[], itemIndex: number): number {
  const item = menuItems[itemIndex];
  const addonsUnit = orderMenuItemExtrasVariationsUnit(item);
  const nonUpgradeModifiersUnit = orderMenuItemNonUpgradeModifiersUnit(item);
  const upgradeUnit = getOrderItemUpgradeModifiers(item).reduce(
    (sum, upgradeRow) => sum + upgradeRow.price,
    0
  );

  if (hasMenuUpgradeMainLine(item)) {
    return item.price + addonsUnit + nonUpgradeModifiersUnit + upgradeUnit;
  }

  const precedingMainIndex = findPrecedingUpgradeMainIndex(menuItems, itemIndex);
  const runningTotal = bundledFixedDiscountRunningTotal(item);
  const followsUpgradeBundle =
    precedingMainIndex != null &&
    runningTotal != null &&
    canApplyUpgradeBundleAdditivePriceShape(item);

  if (followsUpgradeBundle) {
    const previousRunning = cumulativeBundlePrevAmount(menuItems, precedingMainIndex, itemIndex);
    const rawAddonNet =
      item.price + addonsUnit + nonUpgradeModifiersUnit - (runningTotal - previousRunning);
    return Math.max(0, rawAddonNet);
  }

  return item.price + addonsUnit + nonUpgradeModifiersUnit + upgradeUnit;
}

export function getOrderMenuItemLineTotal(
  menuItems: OrderMenuItem[],
  itemIndex: number,
  quantity: number
): number {
  return getOrderMenuItemUnitNet(menuItems, itemIndex) * quantity;
}

export function getOrderHistoryPrimaryColumnMoney(
  menuItems: OrderMenuItem[],
  itemIndex: number,
  quantity: number
): number {
  const item = menuItems[itemIndex];
  const precedingMainIndex = findPrecedingUpgradeMainIndex(menuItems, itemIndex);
  const runningTotal = bundledFixedDiscountRunningTotal(item);
  const followsUpgradeBundle =
    precedingMainIndex != null &&
    runningTotal != null &&
    canApplyUpgradeBundleAdditivePriceShape(item);

  if (followsUpgradeBundle) {
    const previousRunning = cumulativeBundlePrevAmount(menuItems, precedingMainIndex, itemIndex);
    const rawAddonNetUnit = item.price - (runningTotal - previousRunning);
    return Math.max(0, rawAddonNetUnit) * quantity;
  }

  return item.price * quantity;
}
